import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MiniCart } from "./MiniCart";
import { SearchBar } from "./SearchBar";
import { AccountImpersonationBanner } from "@ten24/slatwall-storefront-react-private/global";
import { useSocialLinks } from "../../hooks/components/Social/useSocialLinks";

const HeaderTopLeft = ({ theme, siteSetting }) => {

  const { Social } = useSocialLinks(
    siteSetting,
    theme.headerBarTextColour?.trim(),
    false
  );

  return (
    <div className="d-flex align-items-center">
      {theme.headerStyleOption === "one" && (
        <div className="d-none d-lg-flex align-items-center">
          <Social />
          <DisplayField
            fieldValue={siteSetting.companyPhone}
            iconClass={"telephone-fill"}
          />
          <DisplayField
            fieldValue={siteSetting.companyAddress}
            iconClass={"pin-map-fill"}
          />
        </div>
      )}
      {theme.headerStyleOption === "three" && (
        <div className="d-none d-lg-flex align-items-center my-1">
          <SearchBar searchBoxPlaceholder={theme.searchPlaceholder} />
        </div>
      )}
    </div>
  );
};

const HeaderTopRight = ({ ribbons, theme, siteSetting }) => {
  return (
    <div className="d-flex flex-wrap">
      <div className="d-flex align-items-center">{ribbons}</div>
      <div
        className="text-black d-flex align-items-center"
        style={{ color: theme.headerBarTextColour?.trim() }}
      >
        {theme.headerStyleOption !== "one" &&
          siteSetting.sitePurpose !== "Non-Transactional" && (
            <div className="ms-3 d-none d-lg-block">
              <MiniCart color={theme.headerBarTextColour?.trim()} />
            </div>
          )}
      </div>
    </div>
  );
};

const HeaderContentOne = ({
  theme,
  siteSetting,
  filteredMenus = [],
}) => {
  return (
    <div className="d-flex flex-column align-items-end col-xl-8 order-xl-2">
      <div className="d-none d-lg-flex justify-content-end col-9">
        <SearchBar searchBoxPlaceholder={theme.searchPlaceholder} />
        <div className="ms-2 d-none d-lg-block">
          {siteSetting.sitePurpose !== "Non-Transactional" && <MiniCart />}
        </div>
      </div>

      <div className="text-center text-md-start mb-3 mb-lg-0 order-xl-3 d-none d-lg-block">
        {filteredMenus}
      </div>
    </div>
  );
};

const HeaderContentTwo = ({ theme, siteSetting }) => {
  const { Social } = useSocialLinks(
    siteSetting,
    theme.headerTextColor?.trim(),
    false
  );
  return (
    <div className="d-none d-lg-flex flex-column align-items-start small order-xl-2">
      <div className="d-flex">
        <DisplayField
          fieldValue={siteSetting.companyPhone}
          iconClass={"telephone-fill"}
        />
        <Social />
      </div>
      <DisplayField
        fieldValue={siteSetting.companyEmailAddress}
        iconClass={"at"}
      />
      <DisplayField
        fieldValue={siteSetting.companyAddress}
        iconClass={"pin-map-fill"}
      />
    </div>
  );
};

const DisplayField = ({ fieldValue, iconClass }) => {
  return (
    <>
      {fieldValue && (
        <div className="me-2 d-flex align-items-center">
          <i className={`bi bi-${iconClass} ms-sm-3 me-2 fs-5`}></i>
          <span>{fieldValue}</span>
        </div>
      )}
    </>
  );
};

const MobileNav = ({
  theme,
  filteredMenus = [],
  megaMenus = [],
  SearchBar,
  MiniCart,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="collapse navbar-collapse main-nav no-print"
      id="navbarCollapse"
    >
      {/*  Mobile */}
      <div className="d-lg-none mobile-nav">
        <div className="mobile-nav-header">
          <h2 className="border-bottom">{t("frontend.nav.menu")}</h2>
          <button
            className="navbar-toggler collapsed"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarCollapse"
            aria-controls="navbarCollapse"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="d-flex flex-column align-items-start nav-children mb-3 mb-lg-0 order-xl-3">
          <SearchBar searchBoxPlaceholder={theme.searchPlaceholder} />
          <MiniCart />
        </div>
        {[...filteredMenus, ...megaMenus]}
      </div>
      {/*  default */}
      <div className="d-none d-lg-block">{megaMenus}</div>
    </div>
  );
};

const HeaderContent = ({
  ribbons,
  megaMenus = [],
  filteredMenus = [],
  theme,
  siteSetting,
  selectedCartOrQuote,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <AccountImpersonationBanner />
      <div
        className="header-ribbon"
        style={{
          backgroundColor: theme.headerBarColor?.trim(),
          color: theme.headerBarTextColour?.trim(),
        }}
      >
        <div className="d-flex justify-content-between container">
          <HeaderTopLeft
            theme={theme}
            siteSetting={siteSetting}
          />
          <HeaderTopRight
            theme={theme}
            siteSetting={siteSetting}
            ribbons={ribbons}
          />
        </div>
      </div>
      <nav className="py-3 no-print main-nav">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div
              className={`text-center text-md-start mb-3 mb-lg-0 order-xl-1 ${
                theme.headerStyleOption === "two"
                  ? "col-xl-9 col-lg-9"
                  : "col-xl-4 col-lg-4"
              }`}
            >
              <div className="d-flex filtered-block-div">
                <Link className="d-block" to="/">
                  <span className="navbar-brand d-block">
                    {theme && theme.logo ? (
                      <img
                        src={`${theme.logo?.trim()}`}
                        className="img-fluid navbar-brandLogo"
                        alt={t("frontend.logo")}
                        style={{ maxHeight: "60px", minWidth: "150px" }}
                      />
                    ) : null}
                  </span>
                </Link>
                {theme.headerStyleOption !== "two" && (
                  <div className="d-xl-flex d-none col-xl-6 flex-wrap">
                    {theme.headerTagLine}
                  </div>
                )}
                {theme.headerStyleOption === "two" && (
                  <div className="d-flex flex-column justify-content-center">
                    <div className="d-xl-flex d-none">
                      {theme.headerTagLine}
                    </div>
                    <div className="d-none d-lg-flex">
                      <SearchBar
                        searchBoxPlaceholder={theme.searchPlaceholder}
                      />
                    </div>
                  </div>
                )}
              </div>
              <button
                className="navbar-toggler collapsed align-items-end d-lg-none"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarCollapse"
                aria-controls="navbarCollapse"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <i className="bi bi-list"></i>
              </button>
            </div>
            {theme.headerStyleOption === "one" && (
              <HeaderContentOne
                theme={theme}
                selectedCartOrQuote={selectedCartOrQuote}
                siteSetting={siteSetting}
                filteredMenus={filteredMenus}
              />
            )}
            {theme.headerStyleOption !== "one" && (
              <HeaderContentTwo theme={theme} siteSetting={siteSetting}/>
            )}
          </div>
        </div>
      </nav>
      <MobileNav
        theme={theme}
        filteredMenus={filteredMenus}
        megaMenus={megaMenus}
        SearchBar={SearchBar}
        MiniCart={MiniCart}
      />
    </>
  );
};

export { HeaderContent };
